import React, { useState, useCallback } from 'react';
import { getAuth } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import ClientLayout from '../../../components/layout/ClientLayout';
import { useNavigate } from 'react-router-dom';
import { useEmailVerification } from '../../../hooks/useEmailVerification';
import { useApplicationData } from './DetailPage/hooks/useApplicationData';
import { useApplicationEdit } from './DetailPage/hooks/useApplicationEdit';
import { useDocumentDownload } from './DetailPage/hooks/useDocumentDownload';
import type { ApplicationData, DocumentInfo } from './DetailPage/types';
import {
  ApplicationHeader,
  ApplicantInfoSection,
  DocumentListSection,
  AdditionalRequestsSection,
  ApplicationTimestamps,
  ActionButtons,
  ConfirmModal,
  LoadingSkeleton
} from './DetailPage/components';

const DetailPage: React.FC = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const { loading: emailVerificationLoading } = useEmailVerification();

  // 커스텀 훅 사용
  const {
    application,
    loading,
    error: dataError,
    documentUrls,
    waitingPosition,
    setApplication,
    setDocumentUrls,
    setError: setDataError
  } = useApplicationData();

  const {
    isEditing,
    editedData,
    newDocuments,
    isSubmitting,
    handleEdit,
    handleCancelEdit,
    handleInputChange,
    handleFileChange,
    handleDeleteFile,
    handleSubmit,
    error: editError,
    setError: setEditError
  } = useApplicationEdit(application, setApplication, setDocumentUrls, documentUrls);

  const {
    downloading,
    downloadDocument,
    error: downloadError,
    setError: setDownloadError
  } = useDocumentDownload();

  // 통합 에러 관리 (dataError와 downloadError만 전체 페이지를 덮음)
  const error = dataError || downloadError;
  const setError = (err: string | null) => {
    setDataError(err);
    setDownloadError(err);
  };

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const handleCancelApplication = useCallback(async () => {
    if (!application) return;
    setConfirmAction(() => async () => {
      try {
        setIsCancelling(true);
        const applicationRef = doc(db, 'portville_applications', application.id);
        const now = new Date();

        await updateDoc(applicationRef, {
          status: 'rejected',
          updatedAt: now
        });

        // 상태 업데이트
        setApplication(prev => prev ? {
          ...prev,
          status: 'rejected',
          updatedAt: now
        } : null);

        setError(null);
        // 신청 취소 후 PortvillePage로 리다이렉트
        navigate('/portville');
      } catch (err) {
        console.error('신청 취소 중 오류 발생:', err);
        setError('신청 취소 중 오류가 발생했습니다.');
      } finally {
        setIsCancelling(false);
        setShowConfirmModal(false);
      }
    });
    setShowConfirmModal(true);
  }, [application, navigate, setApplication, setError]);

  const handleConfirm = useCallback(() => {
    if (confirmAction) {
      confirmAction();
    }
  }, [confirmAction]);


  if (loading || emailVerificationLoading) {
    return (
      <ClientLayout>
        <LoadingSkeleton />
      </ClientLayout>
    );
  }

  if (error) {
    return (
      <ClientLayout>
        <div className="flex flex-col items-center justify-center min-h-screen p-6">
          <div className="bg-red-50 rounded-lg p-6 max-w-md w-full">
            <p className="text-red-600 text-center">{error}</p>
          </div>
        </div>
      </ClientLayout>
    );
  }

  if (!application) {
    return (
      <ClientLayout>
        <div className="flex flex-col items-center justify-center min-h-screen p-6">
          <div className="bg-yellow-50 rounded-lg p-6 max-w-md w-full">
            <p className="text-yellow-600 text-center">신청 내역이 없습니다.</p>
          </div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="w-full min-h-screen bg-white">
        <div className="w-[calc(100%-32px)] md:w-[760px] mx-auto relative pt-4 md:pt-8 px-4 md:px-0 pb-24 md:pb-28">
          <ApplicationHeader
            status={application.status}
            waitingPosition={waitingPosition}
            isEditing={isEditing}
            onEdit={handleEdit}
          />

          <div className="w-full h-px mt-6 md:mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>

          <div className="mt-6 md:mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
            <ApplicantInfoSection
              application={application}
              isEditing={isEditing}
              editedData={editedData}
              onInputChange={handleInputChange}
            />

            <DocumentListSection
              documentUrls={documentUrls}
              isEditing={isEditing}
              downloading={downloading}
              onFileChange={handleFileChange}
              onDeleteFile={handleDeleteFile}
              onDownload={downloadDocument}
              applicationName={application.name}
              newDocuments={newDocuments}
              fileError={editError}
            />

            <AdditionalRequestsSection
              application={application}
              isEditing={isEditing}
              editedData={editedData}
              onInputChange={handleInputChange}
            />
          </div>

          <ApplicationTimestamps
            createdAt={application.createdAt}
            updatedAt={application.updatedAt}
          />

          <ActionButtons
            status={application.status}
            isEditing={isEditing}
            isSubmitting={isSubmitting}
            isCancelling={isCancelling}
            onCancelEdit={handleCancelEdit}
            onSubmit={handleSubmit}
            onCancelApplication={handleCancelApplication}
          />
        </div>

        <ConfirmModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={handleConfirm}
          title="신청 취소 확인"
          message="정말 신청을 취소하시겠습니까? 이 작업은 되돌릴 수 없습니다."
        />
      </div>
    </ClientLayout>
  );
};

export default DetailPage;